
import React, { useEffect, useState, useRef } from 'react';
import { DailyGuidance, UserProfile, DailyCheckIn, ChatMessage, AppView } from '../../types';
import { generateDailyGuidance, getGeneralCoachResponse } from '../../services/contentService';
import { guidanceApi, checkinsApi, searchApi, appointmentsApi, userApi } from '../../services/apiService';
import { calculatePregnancyWeek } from '../../utils/dateUtils';
import { getPartnerTipForWeek, SUPPORT_SCRIPTS, PARTNER_SELF_CARE, WEEKLY_PARTNER_TIPS } from '../../constants/partnerCoaching';
import { getPregnancyValidation, getDayContent, getPregnancyTipForWeek, PREGNANCY_VALIDATIONS } from '../../constants/pregnancyContent';
import { Logo } from '../ui/Logo';
import { BabyVisualizer } from '../visuals/BabyVisualizer';
import { FlashCards } from '../ui/FlashCards';
import { Loader2, MessageSquare, Coffee, CheckSquare, Sparkles, X, MessageCircle, Send, Baby, Lock, Share2, HeartHandshake, ChevronLeft, ChevronRight, CalendarOff, Settings, BrainCircuit, Heart, Calendar, Clock, Stethoscope, Palette, Check } from 'lucide-react';
import { Appointment } from '../../types';

interface DashboardProps {
  user: UserProfile;
  onNavigateToSettings: () => void;
  onNavigateToTool?: (tool: string) => void;
  onUserUpdate?: (user: UserProfile) => void;
}

export const Dashboard: React.FC<DashboardProps> = ({ user, onNavigateToSettings, onNavigateToTool, onUserUpdate }) => {
  const [viewDate, setViewDate] = useState(new Date());
  const [guidance, setGuidance] = useState<DailyGuidance | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [currentWeek, setCurrentWeek] = useState(0);
  const [nextAppointment, setNextAppointment] = useState<Appointment | null>(null);
  const [daysUntilDue, setDaysUntilDue] = useState(0);

  // Check-In State
  const [showCheckIn, setShowCheckIn] = useState(false);
  const [myState, setMyState] = useState('');
  const [partnerSymptoms, setPartnerSymptoms] = useState<string[]>([]);
  const [contextTags, setContextTags] = useState<string[]>([]);
  const [isShared, setIsShared] = useState(true);

  // Coach Chat State
  const [showCoach, setShowCoach] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Theme Picker State
  const [showThemePicker, setShowThemePicker] = useState(false);
  const [currentTheme, setCurrentTheme] = useState<'fruit' | 'sports' | 'geek' | 'tools' | 'snacks'>(
    (user.comparisonTheme as 'fruit' | 'sports' | 'geek' | 'tools' | 'snacks') || 'fruit'
  );
  const [savingTheme, setSavingTheme] = useState(false);

  const isPartner = user.role === 'partner';

  // Unified theme configuration (same for both roles)
  const theme = {
    actionCardBg: 'bg-emerald-50/50',
    actionCardBorder: 'border-emerald-100',
    actionIconColor: 'text-emerald-600',
    actionBadgeBg: 'bg-emerald-100',
    actionBadgeText: 'text-emerald-700',

    scriptCardBg: 'bg-blue-50/50',
    scriptCardBorder: 'border-blue-100',
    scriptIconColor: 'text-blue-600',
    scriptBadgeBg: 'bg-blue-100',
    scriptBadgeText: 'text-blue-700',
    scriptBoxBg: 'bg-white/80 border-blue-100/50 text-blue-900',

    checkInSelectedBg: 'bg-emerald-600',
    checkInSelectedText: 'text-white',
    checkInSelectedTagBg: 'bg-emerald-50',
    checkInSelectedTagBorder: 'border-emerald-200',
    checkInSelectedTagText: 'text-emerald-700',
  };

  // Helper to format date key YYYY-MM-DD locally
  const getDateKey = (d: Date) => {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const isToday = (d: Date) => {
    return getDateKey(d) === getDateKey(new Date());
  };

  // Calculate days until due date
  useEffect(() => {
    const dueDate = new Date(user.dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    const diffTime = dueDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    setDaysUntilDue(diffDays);
  }, [user.dueDate]);

  // Load next appointment
  useEffect(() => {
    const loadNextAppointment = async () => {
      try {
        const appointments = await appointmentsApi.list();
        const today = getDateKey(new Date());

        // Find the next upcoming appointment
        const upcoming = appointments
          .filter(apt => apt.date >= today)
          .sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time));

        setNextAppointment(upcoming.length > 0 ? upcoming[0] : null);
      } catch (error) {
        console.error('Failed to load appointments:', error);
      }
    };
    loadNextAppointment();
  }, []);

  // Load Data when Date Changes
  useEffect(() => {
    const loadData = async () => {
      const week = calculatePregnancyWeek(user.dueDate, viewDate);
      setCurrentWeek(week);

      const dateKey = getDateKey(viewDate);
      setLoadingData(true);

      try {
        // Load from D1 database
        const existingData = await guidanceApi.getByDate(dateKey);

        // Only use cached guidance if it matches the current week
        // (Prevents showing stale AI-generated content from old system)
        if (existingData && existingData.week === week) {
          setGuidance(existingData);
          setShowCheckIn(false);
        } else {
          setGuidance(null);
          // Don't auto-show check-in - partners have static content, 
          // and pregnant users can tap to open it
          setShowCheckIn(false);
        }
      } catch (error) {
        console.error('Failed to load guidance:', error);
        setGuidance(null);
        setShowCheckIn(isToday(viewDate));
      } finally {
        setLoadingData(false);
      }
    };

    loadData();
  }, [viewDate, user.dueDate]);

  useEffect(() => {
    if (showCoach) {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, showCoach]);

  const changeDay = (offset: number) => {
    const newDate = new Date(viewDate);
    newDate.setDate(viewDate.getDate() + offset);

    // Prevent going to future
    if (newDate > new Date()) return;

    setViewDate(newDate);
  };

  const handleGenerate = async () => {
    setLoading(true);
    setShowCheckIn(false);

    const dateKey = getDateKey(new Date());

    const checkIn: DailyCheckIn = {
      date: dateKey,
      myState,
      partnerSymptoms,
      contextTags
    };

    try {
      // Save check-in to D1 database
      await checkinsApi.save(checkIn);

      // Generate AI guidance
      const data = await generateDailyGuidance(
        currentWeek,
        user.partnerName,
        user.role,
        checkIn,
        user.comparisonTheme || 'fruit'
      );
      setGuidance(data);

      // Save guidance to D1 database
      await guidanceApi.save(dateKey, data);
    } catch (error) {
      console.error('Failed to save data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSymptom = (sym: string) => {
    if (partnerSymptoms.includes(sym)) {
      setPartnerSymptoms(prev => prev.filter(s => s !== sym));
    } else {
      setPartnerSymptoms(prev => [...prev, sym]);
    }
  };

  const toggleContext = (tag: string) => {
    if (contextTags.includes(tag)) {
      setContextTags(prev => prev.filter(t => t !== tag));
    } else {
      setContextTags(prev => [...prev, tag]);
    }
  }

  const handleCoachSend = async (manualInput?: string) => {
    const textToSend = manualInput || chatInput;
    if (!textToSend.trim()) return;

    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      sender: 'user',
      text: textToSend,
      timestamp: Date.now()
    };

    setChatMessages(prev => [...prev, userMsg]);
    setChatInput('');
    setChatLoading(true);

    let response: string;

    if (isPartner) {
      // Use static pre-canned responses from partnerCoaching.ts for partners
      const lowerText = textToSend.toLowerCase();

      // Find matching support script
      const matchedScript = SUPPORT_SCRIPTS.find(script =>
        script.situation.toLowerCase().includes(lowerText.slice(0, 20)) ||
        lowerText.includes('cry') && script.situation.includes('crying') ||
        lowerText.includes('sad') && script.situation.includes('sad') ||
        lowerText.includes('tired') && script.situation.includes('tired') ||
        lowerText.includes('scared') && script.situation.includes('anxious') ||
        lowerText.includes('anxious') && script.situation.includes('anxious') ||
        lowerText.includes('pain') && script.situation.includes('body') ||
        lowerText.includes('back') && script.situation.includes('body') ||
        lowerText.includes('useless') && script.situation.includes('useless')
      );

      if (matchedScript) {
        response = `**${matchedScript.situation}**\n\n` +
          `✅ **Say this:** "${matchedScript.whatToSay[0]}"\n\n` +
          `❌ **Avoid:** "${matchedScript.whatNotToSay[0]}"\n\n` +
          `💡 **Action:** ${matchedScript.actionToTake}`;
      } else {
        // Explicit keyword to week mapping for accurate responses
        const keywordToWeek: { [key: string]: number } = {
          // Sleep & Comfort (week 29)
          'comfortable': 29, 'sleep': 29, 'night': 29, 'insomnia': 29, 'pillow': 29, 'can\'t sleep': 29, 'uncomfortable': 29,
          // Hospital & Car (weeks 31, 35)
          'hospital': 31, 'bag': 31, 'pack': 31,
          'car seat': 35, 'carseat': 35, 'install': 35,
          // Labor (weeks 30, 37, 38)
          'labor': 37, 'contractions': 30, 'signs of labor': 38,
          'braxton': 30, 'hicks': 30, 'practice contraction': 30,
          // First trimester symptoms
          'nausea': 5, 'morning sickness': 5, 'sick': 5,
          'exhausted': 6, 'tired': 6, 'fatigue': 6,
          'food aversions': 7, 'smell': 7, 'cook': 7,
          'first appointment': 8, 'heartbeat': 8,
          'emotional': 9, 'mood swings': 9, 'crying': 9,
          'secret': 10, 'telling people': 10, 'announce': 12,
          // Second trimester
          'bump': 14, 'showing': 14,
          'movement': 15, 'flutter': 15,
          'gender': 16, 'find out': 16, 'sex': 16,
          'back pain': 17, 'aches': 17, 'round ligament': 17, 'leg cramps': 17,
          'anatomy': 18, 'scan': 18, 'ultrasound': 8,
          'nursery': 19, 'nesting': 19,
          'halfway': 20, 'midpoint': 20,
          'kicks': 21, 'feel baby': 21,
          'names': 22, 'baby name': 22, 'naming': 22,
          'shower': 32, 'baby shower': 32, 'registry': 20,
          'viability': 24, 'viable': 24,
          'glucose': 25, 'sugar test': 25, 'gestational diabetes': 25,
          // Third trimester
          'third trimester': 27, 'birth class': 27,
          'appointment': 28, 'prenatal': 28,
          'birth plan': 33, 'class': 33,
          'work': 34, 'paternity': 34, 'leave': 34,
          'full term': 37, 'term': 37,
          'false alarm': 38, 'false labor': 38,
          'due date': 40, 'overdue': 40, 'past due': 40, 'waiting': 40,
        };

        // Find matching week from keywords
        let matchedWeek: number | null = null;
        for (const [keyword, week] of Object.entries(keywordToWeek)) {
          if (lowerText.includes(keyword)) {
            matchedWeek = week;
            break;
          }
        }

        const matchedTip = matchedWeek
          ? WEEKLY_PARTNER_TIPS.find(tip => tip.week === matchedWeek)
          : null;

        if (matchedTip) {
          response = `**Week ${matchedTip.week}: ${matchedTip.title}**\n\n` +
            `${matchedTip.mainTip}\n\n` +
            `📋 **Do this:** ${matchedTip.doThis[0]}\n\n` +
            `💬 **Say this:** "${matchedTip.sayThis}"\n\n` +
            `🧘 **For you:** ${matchedTip.selfCare}`;
        } else {
          // No keyword match - fall back to web search
          const searchResult = await searchApi.query(textToSend, 'partner', currentWeek);
          response = searchResult.answer;
        }
      }

      // Simulate brief delay for UX
      await new Promise(r => setTimeout(r, 500));
    } else {
      // Pregnancy role - use static validation content
      const lowerText = textToSend.toLowerCase();
      const dayContent = getDayContent();
      const weekTip = getPregnancyTipForWeek(currentWeek);

      // Find matching validation
      const matchedValidation = getPregnancyValidation(textToSend);

      if (matchedValidation) {
        response = `**${matchedValidation.situation}**\n\n` +
          `💜 **You're not alone:** ${matchedValidation.validation[0]}\n\n` +
          `✅ **Try this:** ${matchedValidation.practicalTips[0]}\n\n` +
          `🧘 **Self-care:** ${matchedValidation.selfCare}`;
      } else if (weekTip) {
        // Default to weekly pregnancy content with day theme
        const trimesterNum = currentWeek < 14 ? 1 : currentWeek < 27 ? 2 : 3;

        response = `**Week ${weekTip.week}: ${weekTip.title}**\n\n` +
          `🗣️ **Your body:** ${weekTip.bodyUpdate}\n\n` +
          `👶 **Baby update:** ${weekTip.babyUpdate}\n\n` +
          `🧘 **For you:** ${weekTip.selfCareTip}\n\n` +
          `_${dayContent.theme}: ${dayContent.pregnancyFocus}_`;
      } else {
        // Fallback to web search for unmatched queries
        const searchResult = await searchApi.query(textToSend, 'pregnant', currentWeek);
        response = searchResult.answer;
      }

      await new Promise(r => setTimeout(r, 500));
    }

    const aiMsg: ChatMessage = {
      id: (Date.now() + 1).toString(),
      sender: 'ai',
      text: response,
      timestamp: Date.now()
    };

    setChatMessages(prev => [...prev, aiMsg]);
    setChatLoading(false);
  }

  // Get tips for current week based on role
  const weeklyPartnerTip = isPartner ? getPartnerTipForWeek(currentWeek) : null;
  const weeklyPregnancyTip = !isPartner ? getPregnancyTipForWeek(currentWeek) : null;
  const trimester = currentWeek < 14 ? 1 : currentWeek < 27 ? 2 : 3;
  const trimesterLabel = trimester === 1 ? '1st Trimester' : trimester === 2 ? '2nd Trimester' : '3rd Trimester';

  // Trimester-specific starter prompts for partners
  const getPartnerStarters = () => {
    const baseStarters = [
      weeklyPartnerTip?.sayThis ? `How to say: "${weeklyPartnerTip.sayThis.substring(0, 25)}..."` : null,
    ].filter(Boolean) as string[];

    const trimesterStarters = trimester === 1 ? [
      "She's exhausted and nauseous",
      "What foods help with morning sickness?",
      "She's emotional - what do I say?",
      "How can I help when she's tired?",
      "What should I avoid saying?",
    ] : trimester === 2 ? [
      "She's anxious about the anatomy scan",
      "Her back is killing her",
      "How do I talk to the baby bump?",
      "She's worried about weight gain",
      "What can I do for leg cramps?",
    ] : [
      "She can't get comfortable at night",
      "What should I pack in the hospital bag?",
      "She's scared about labor",
      "How do I help with Braxton Hicks?",
      "When should we go to the hospital?",
    ];

    return [...baseStarters, ...trimesterStarters].slice(0, 6);
  };

  // Trimester-specific starters for pregnancy role
  const getPregnancyStarters = () => {
    // General starters (always shown)
    const generalStarters = [
      "What's happening this week?",
      "I need some encouragement",
    ];

    const trimesterStarters = trimester === 1 ? [
      "Is this nausea normal?",
      "I'm so exhausted all the time",
      "I'm scared of miscarriage",
      "Food aversions are intense",
    ] : trimester === 2 ? [
      "Is my baby moving enough?",
      "I'm anxious about the anatomy scan",
      "My body is changing so much",
      "I'm worried about the glucose test",
    ] : [
      "I can't sleep at all",
      "I'm scared about labor",
      "Everything hurts right now",
      "When should I go to the hospital?",
    ];
    return [...generalStarters, ...trimesterStarters].slice(0, 6);
  };

  // Week-specific intro for pregnancy role
  const getPregnancyIntro = () => {
    if (trimester === 1) {
      return `Week ${currentWeek}: Your body is working hard building a placenta and supporting early development. Fatigue and nausea are real - you're not imagining it.`;
    } else if (trimester === 2) {
      return `Week ${currentWeek}: The honeymoon trimester! Energy often returns, but every pregnancy is different. Baby is growing fast and you may start feeling movements.`;
    } else {
      return `Week ${currentWeek}: The home stretch! Your body is preparing for birth. Rest when you can, and know that discomfort at this stage is normal and temporary.`;
    }
  };

  // Role Specific Chat Configurations
  const coachConfig = isPartner ? {
    title: "Partner Coach",
    subtitle: `Week ${currentWeek} • ${trimesterLabel}`,
    intro: weeklyPartnerTip?.mainTip || "Tactical advice & support scripts",
    icon: <BrainCircuit className="w-5 h-5 text-indigo-500" />,
    accentColor: "bg-indigo-500",
    starters: getPartnerStarters()
  } : {
    title: "Pregnancy Guide",
    subtitle: `Week ${currentWeek} • ${trimesterLabel}`,
    intro: getPregnancyIntro(),
    icon: <HeartHandshake className="w-5 h-5 text-rose-500" />,
    accentColor: "bg-rose-500",
    starters: getPregnancyStarters()
  };

  if (loading) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-8 text-center bg-stone-50">
        <div className="mb-6 animate-pulse">
          <Logo className="w-16 h-16 shadow-xl rounded-2xl" />
        </div>
        <h3 className="text-xl font-bold text-stone-900 mb-2">Analyzing Context...</h3>
        <p className="text-stone-500">
          Building a personalized support plan for {isPartner ? `you and ${user.partnerName}` : 'you'} based on today's reality.
        </p>
      </div>
    );
  }

  // Check In Overlay (Only for Today)
  if (showCheckIn && isToday(viewDate)) {
    return (
      <div className="h-full bg-stone-50 p-6 flex flex-col overflow-y-auto">
        <div className="mb-6 flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-stone-900">{isPartner ? "Sync Up" : "Daily Check-In"}</h1>
            <p className="text-stone-500">{isPartner ? "Help us help you support her." : "How are things today?"}</p>
          </div>
          <button onClick={() => setShowCheckIn(false)} aria-label="Close check-in" className="p-2 bg-white rounded-full text-stone-400 hover:text-stone-600 shadow-sm">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div className="bg-white p-5 rounded-2xl shadow-sm border border-stone-100">
            <h3 className="font-semibold text-stone-800 mb-3">{isPartner ? "How are YOU?" : "My Mood"}</h3>
            <div className="grid grid-cols-2 gap-2">
              {['Calm', 'Stressed', 'Tired', 'Energized', 'Anxious', 'Overwhelmed'].map(s => (
                <button
                  key={s}
                  onClick={() => setMyState(s)}
                  className={`p-3 rounded-xl text-sm font-medium transition-all ${myState === s ? `${theme.checkInSelectedBg} ${theme.checkInSelectedText} shadow-md` : 'bg-stone-50 text-stone-600 hover:bg-stone-100'}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl shadow-sm border border-stone-100">
            <h3 className="font-semibold text-stone-800 mb-3">
              {isPartner ? `What's ${user.partnerName} experiencing?` : "My Symptoms"}
            </h3>
            <div className="flex flex-wrap gap-2">
              {['Nausea', 'Fatigue', 'Back Pain', 'Cravings', 'Mood Swings', 'Anxiety', 'Insomnia', 'Nothing specific'].map(s => (
                <button
                  key={s}
                  onClick={() => toggleSymptom(s)}
                  className={`px-4 py-2 rounded-full text-sm transition-all border ${partnerSymptoms.includes(s) ? `${theme.checkInSelectedTagBg} ${theme.checkInSelectedTagBorder} ${theme.checkInSelectedTagText}` : 'bg-white border-stone-200 text-stone-600'}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl shadow-sm border border-stone-100">
            <h3 className="font-semibold text-stone-800 mb-3">Context</h3>
            <div className="flex flex-wrap gap-2">
              {['Doctor Appointment', 'Bad Sleep', 'Argument', 'Travel', 'Work Stress', 'Celebration'].map(t => (
                <button
                  key={t}
                  onClick={() => toggleContext(t)}
                  className={`px-4 py-2 rounded-full text-sm transition-all border ${contextTags.includes(t) ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-white border-stone-200 text-stone-600'}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-8 pb-8">
          <button
            onClick={handleGenerate}
            disabled={!myState}
            className="w-full bg-stone-900 text-white h-14 rounded-2xl font-bold text-lg shadow-lg hover:bg-stone-800 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
          >
            <Sparkles className="w-5 h-5" />
            {isPartner ? "Generate Support Plan" : "Update My Day"}
          </button>
        </div>
      </div>
    );
  }

  // Dashboard View (Current or Past)
  return (
    <div className="h-full bg-stone-50 relative flex flex-col overflow-hidden">

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto no-scrollbar">
        {/* Header */}
        <div className="bg-white px-6 pt-6 pb-6 rounded-b-3xl shadow-sm border-b border-stone-100 sticky top-0 z-20">
          {/* Top Bar with Settings */}
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-2">
              <div onClick={onNavigateToSettings} className="relative w-8 h-8 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center font-bold text-xs cursor-pointer active:scale-95 transition-transform group" title="Settings">
                {user.name.charAt(0)}
                <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-white shadow-sm flex items-center justify-center">
                  <Settings size={9} className="text-stone-500" />
                </div>
              </div>
              <span className="text-xs font-bold text-stone-500 uppercase tracking-wider">Week {currentWeek}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => changeDay(-1)}
                aria-label="Previous day"
                className="p-1.5 bg-stone-50 hover:bg-stone-100 rounded-full text-stone-600 transition-colors"
              >
                <ChevronLeft size={20} />
              </button>
              <span className="block font-bold text-sm text-stone-900 w-24 text-center">
                {isToday(viewDate) ? 'Today' : viewDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
              <button
                onClick={() => changeDay(1)}
                disabled={isToday(viewDate)}
                aria-label="Next day"
                className={`p-1.5 rounded-full transition-colors ${isToday(viewDate) ? 'text-stone-200 cursor-not-allowed' : 'bg-stone-50 hover:bg-stone-100 text-stone-600'}`}
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>

          {/* Content Header or Empty State */}
          {guidance ? (
            <div className="animate-fade-in">
              <h1 className="text-3xl font-bold text-stone-900 leading-tight mb-2">{guidance.theme}</h1>
              <p className="text-stone-600 leading-relaxed text-sm">{guidance.context}</p>
            </div>
          ) : isPartner && weeklyPartnerTip ? (
            <div className="animate-fade-in">
              <h1 className="text-3xl font-bold text-stone-900 leading-tight mb-2">{weeklyPartnerTip.title}</h1>
              <p className="text-stone-600 leading-relaxed text-sm">{weeklyPartnerTip.mainTip}</p>
            </div>
          ) : !isPartner && weeklyPregnancyTip ? (
            <div className="animate-fade-in">
              <h1 className="text-3xl font-bold text-stone-900 leading-tight mb-2">{weeklyPregnancyTip.title}</h1>
              <p className="text-stone-600 leading-relaxed text-sm">{weeklyPregnancyTip.bodyUpdate}</p>
            </div>
          ) : (
            <div className="animate-fade-in">
              <h1 className="text-3xl font-bold text-stone-900 leading-tight mb-2">Week {currentWeek}</h1>
              {isToday(viewDate) ? (
                <button
                  onClick={() => setShowCheckIn(true)}
                  className="text-brand-600 hover:text-brand-700 text-sm font-medium underline underline-offset-2"
                >
                  Tap to personalize your daily update →
                </button>
              ) : (
                <p className="text-stone-600 leading-relaxed text-sm">No update recorded for this day.</p>
              )}
            </div>
          )}
        </div>

        <div className="p-6 space-y-6 pb-32 animate-fade-in">

          {/* Quick Stats Row - Countdown + Next Appointment */}
          <div className="grid grid-cols-2 gap-3">
            {/* Countdown Widget */}
            <div className="bg-white p-4 rounded-2xl border border-stone-100 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-amber-500" />
                <span className="text-[10px] font-bold uppercase tracking-wider text-stone-500">Due Date</span>
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-bold text-stone-900">{Math.max(0, daysUntilDue)}</span>
                <span className="text-sm text-stone-500">days</span>
              </div>
              <p className="text-[11px] text-stone-500 mt-1">
                {daysUntilDue > 0
                  ? new Date(user.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  : daysUntilDue === 0
                    ? "Today!"
                    : `${Math.abs(daysUntilDue)} days past`
                }
              </p>
            </div>

            {/* Next Appointment Widget */}
            {nextAppointment ? (
              <button
                onClick={() => onNavigateToTool?.('doc')}
                className="bg-white p-4 rounded-2xl border border-stone-100 shadow-sm text-left active:scale-[0.98] transition-transform"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Stethoscope className="w-4 h-4 text-blue-500" />
                  <span className="text-[10px] font-bold uppercase tracking-wider text-stone-500">Next Appt</span>
                </div>
                <p className="font-bold text-stone-900 text-sm leading-tight line-clamp-1">{nextAppointment.title}</p>
                <div className="flex items-center gap-1.5 mt-1.5 text-[11px] text-stone-500">
                  <Calendar className="w-3 h-3" />
                  <span>{new Date(nextAppointment.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                  <Clock className="w-3 h-3 ml-1" />
                  <span>{nextAppointment.time}</span>
                </div>
              </button>
            ) : (
              <button
                onClick={() => onNavigateToTool?.('doc')}
                className="bg-stone-50 p-4 rounded-2xl border border-dashed border-stone-200 text-left active:scale-[0.98] transition-transform"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Stethoscope className="w-4 h-4 text-stone-500" />
                  <span className="text-[10px] font-bold uppercase tracking-wider text-stone-500">Next Appt</span>
                </div>
                <p className="font-medium text-stone-600 text-sm">No upcoming</p>
                <p className="text-[11px] text-stone-500 mt-1">Tap to add appointment</p>
              </button>
            )}
          </div>

          {/* Living Visualizer Card - Always Visible */}
          <div className="w-full aspect-[4/3] bg-black rounded-3xl relative overflow-hidden shadow-2xl shadow-stone-900/20 group">
            <BabyVisualizer week={currentWeek} />

            <div className="absolute top-0 left-0 p-5 z-10 w-full bg-gradient-to-b from-black/60 to-transparent">
              <div className="flex items-center gap-2">
                <Baby className="w-4 h-4 text-white/80" />
                <span className="text-xs font-bold uppercase tracking-widest text-white/60">The Baby</span>
              </div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black/80 via-black/40 to-transparent z-10">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <h2 className="text-2xl font-bold text-white leading-tight text-shadow-sm">
                    {(() => {
                      // Calculate size based on current week and theme
                      const themeSizes: Record<string, string[]> = {
                        fruit: ["Poppy Seed", "Sesame Seed", "Lentil", "Blueberry", "Raspberry", "Olive", "Fig", "Lime", "Lemon", "Apple", "Avocado", "Onion", "Mango", "Banana", "Pomegranate", "Papaya", "Grapefruit", "Cantaloupe", "Cauliflower", "Coconut", "Pineapple", "Butternut Squash", "Watermelon", "Pumpkin"],
                        geek: ["8-bit Pixel", "Microchip", "D4 Die", "D20 Die", "USB Drive", "GameBoy Cartridge", "Mouse", "Smartphone", "GameBoy Color", "Communicator", "Phaser", "Sonic Screwdriver", "Pip-Boy", "Lightsaber Hilt", "VR Headset", "Droid", "Keyboard", "Laptop", "Console", "Desktop Tower"],
                        sports: ["BB Pellet", "Airsoft Pellet", "Golf Tee", "Marble", "Poker Chip", "Golf Ball", "Squash Ball", "Ping Pong Ball", "Tennis Ball", "Baseball", "Cricket Ball", "Softball", "Hockey Puck", "Curling Stone", "Volleyball", "Bowling Ball", "Basketball", "Beach Ball", "Medicine Ball"],
                        tools: ["Small Washer", "Screw Head", "Nut", "Bolt", "Allen Key", "Socket", "Spark Plug", "Tape Measure", "Screwdriver Handle", "Wrench", "Pliers", "Hammer Head", "Drill Battery", "Multimeter", "Angle Grinder", "Circular Saw Blade", "Drill", "Toolbox", "Shop Vac", "Compressor"],
                        snacks: ["Tic Tac", "Sprinkle", "Skittle", "M&M", "Gummy Bear", "Oreo", "Reese's Cup", "Granola Bar", "Pop-Tart", "Donut", "Bagel", "Burrito", "Sub Sandwich", "Large Pizza Slice", "Bucket of Popcorn", "Party Size Chips", "Cake", "Watermelon (Whole)", "Thanksgiving Turkey"]
                      };
                      const sizes = themeSizes[currentTheme] || themeSizes.fruit;
                      const sizeIndex = Math.min(Math.max(0, Math.floor((currentWeek - 4) / 1.6)), sizes.length - 1);
                      return currentWeek < 4 ? "Microscopic" : `Size of a ${sizes[sizeIndex]}`;
                    })()}
                  </h2>
                  <button
                    onClick={() => setShowThemePicker(true)}
                    className="shrink-0 p-1.5 rounded-lg bg-white/20 backdrop-blur-md text-white/80 border border-white/10 hover:bg-white/30 active:scale-95 transition-all"
                    title="Change size comparison theme"
                  >
                    <Palette size={14} />
                  </button>
                </div>
                <p className="text-sm text-white/80 leading-relaxed drop-shadow-md">
                  {guidance?.babyDev || (() => {
                    // Week-based baby development descriptions
                    const weeklyDev: Record<number, string> = {
                      4: "Cells are rapidly dividing. The neural tube is forming.",
                      5: "The heart begins to beat. Tiny buds for arms and legs appear.",
                      6: "Facial features start forming. The brain is developing rapidly.",
                      7: "Arms and legs are growing. Fingers and toes are starting to form.",
                      8: "All major organs are in place. The tail is disappearing.",
                      9: "Muscles are forming. Baby can make small movements.",
                      10: "Vital organs are functioning. Fingernails are developing.",
                      11: "Baby is now officially a fetus. Bones are hardening.",
                      12: "Reflexes are developing. Baby can open and close fingers.",
                      13: "Fingerprints are forming. Vocal cords are developing.",
                      14: "Baby can squint and frown. The body is catching up to the head.",
                      15: "Baby is practicing breathing. Legs are longer than arms now.",
                      16: "Facial muscles allow expressions. Baby can grip strongly.",
                      17: "Fat is starting to form. Baby can hear your voice.",
                      18: "Baby is actively moving. The nervous system is maturing.",
                      19: "A protective coating covers baby's skin. Senses are developing.",
                      20: "You're halfway there! Baby swallows amniotic fluid.",
                      21: "Eyebrows and eyelids are fully formed. Baby is very active.",
                      22: "Baby's grip is very strong. Taste buds are forming.",
                      23: "Baby can hear sounds from outside. Skin is developing pigment.",
                      24: "Lungs are developing rapidly. Baby has a regular sleep schedule.",
                      25: "Baby responds to your voice. Body fat is increasing.",
                      26: "Eyes can open now. Baby can hiccup!",
                      27: "Baby's brain is very active. Sleep cycles include REM sleep.",
                      28: "Baby can blink and has eyelashes. Vision is developing.",
                      29: "Baby's muscles and lungs are maturing. Movement is strong.",
                      30: "Baby is running out of room. Brain tissue is increasing.",
                      31: "Baby can turn head. All five senses are working.",
                      32: "Skin is becoming less wrinkled. Baby practices breathing.",
                      33: "Baby recognizes familiar sounds. Skull bones are still soft.",
                      34: "Fingernails reach fingertips. Immune system is developing.",
                      35: "Baby is gaining weight quickly. Kidneys are fully developed.",
                      36: "Baby has dropped lower in preparation. Lungs are nearly mature.",
                      37: "Baby is considered early term. Practicing for the big day!",
                      38: "Baby is full term. Organs are ready for life outside.",
                      39: "Baby continues to build fat. Could arrive any day!",
                      40: "Baby is fully developed and ready to meet you!"
                    };
                    return weeklyDev[currentWeek] || `Week ${currentWeek}: Baby is growing and developing!`;
                  })()}
                </p>
              </div>
            </div>
          </div>

          {/* Theme Picker Modal */}
          {showThemePicker && (
            <div 
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" 
              onClick={() => setShowThemePicker(false)}
            >
              <div
                className="bg-white w-full max-w-sm rounded-2xl shadow-2xl animate-fade-in max-h-[85vh] overflow-hidden flex flex-col"
                onClick={e => e.stopPropagation()}
              >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-stone-100 shrink-0">
                  <div className="flex items-center gap-2">
                    <Palette size={20} className="text-brand-600" />
                    <h3 className="font-bold text-stone-900">Size Theme</h3>
                  </div>
                  <button
                    onClick={() => setShowThemePicker(false)}
                    className="p-2 bg-stone-100 rounded-full text-stone-500 hover:bg-stone-200 transition-colors"
                    aria-label="Close theme picker"
                  >
                    <X size={18} />
                  </button>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                  <p className="text-sm text-stone-500 mb-3">Choose how to visualize your baby's size each week.</p>
                  {(['fruit', 'sports', 'geek', 'tools', 'snacks'] as const).map(themeOption => {
                    // Preview sizes for current week
                    const themeSizes: Record<string, string[]> = {
                      fruit: ["Poppy Seed", "Sesame Seed", "Lentil", "Blueberry", "Raspberry", "Olive", "Fig", "Lime", "Lemon", "Apple", "Avocado", "Onion", "Mango", "Banana", "Pomegranate", "Papaya", "Grapefruit", "Cantaloupe", "Cauliflower", "Coconut", "Pineapple", "Butternut Squash", "Watermelon", "Pumpkin"],
                      geek: ["8-bit Pixel", "Microchip", "D4 Die", "D20 Die", "USB Drive", "GameBoy Cartridge", "Mouse", "Smartphone", "GameBoy Color", "Communicator", "Phaser", "Sonic Screwdriver", "Pip-Boy", "Lightsaber Hilt", "VR Headset", "Droid", "Keyboard", "Laptop", "Console", "Desktop Tower"],
                      sports: ["BB Pellet", "Airsoft Pellet", "Golf Tee", "Marble", "Poker Chip", "Golf Ball", "Squash Ball", "Ping Pong Ball", "Tennis Ball", "Baseball", "Cricket Ball", "Softball", "Hockey Puck", "Curling Stone", "Volleyball", "Bowling Ball", "Basketball", "Beach Ball", "Medicine Ball"],
                      tools: ["Small Washer", "Screw Head", "Nut", "Bolt", "Allen Key", "Socket", "Spark Plug", "Tape Measure", "Screwdriver Handle", "Wrench", "Pliers", "Hammer Head", "Drill Battery", "Multimeter", "Angle Grinder", "Circular Saw Blade", "Drill", "Toolbox", "Shop Vac", "Compressor"],
                      snacks: ["Tic Tac", "Sprinkle", "Skittle", "M&M", "Gummy Bear", "Oreo", "Reese's Cup", "Granola Bar", "Pop-Tart", "Donut", "Bagel", "Burrito", "Sub Sandwich", "Large Pizza Slice", "Bucket of Popcorn", "Party Size Chips", "Cake", "Watermelon (Whole)", "Thanksgiving Turkey"]
                    };
                    const sizes = themeSizes[themeOption];
                    const sizeIndex = Math.min(Math.max(0, Math.floor((currentWeek - 4) / 1.6)), sizes.length - 1);
                    const previewSize = currentWeek < 4 ? "Microscopic" : sizes[sizeIndex];

                    return (
                      <button
                        key={themeOption}
                        onClick={() => setCurrentTheme(themeOption)}
                        className={`w-full p-3 rounded-xl border-2 text-left transition-all flex items-center justify-between active:scale-[0.98] ${currentTheme === themeOption
                          ? 'border-brand-500 bg-brand-50'
                          : 'border-stone-200 bg-white hover:border-stone-300'
                          }`}
                      >
                        <div className="min-w-0 flex-1">
                          <span className="font-semibold text-stone-900 capitalize">{themeOption}</span>
                          <p className="text-xs text-stone-500 mt-0.5 truncate">Week {currentWeek}: <span className="font-medium text-stone-700">{previewSize}</span></p>
                        </div>
                        {currentTheme === themeOption && (
                          <Check size={18} className="text-brand-600 shrink-0 ml-2" />
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Footer with Save Button */}
                <div className="p-4 border-t border-stone-100 shrink-0 bg-white">
                  <button
                    onClick={async () => {
                      setSavingTheme(true);
                      try {
                        const updatedUser = await userApi.updateProfile({ comparisonTheme: currentTheme as any });
                        onUserUpdate?.(updatedUser);

                        // Immediately update the displayed size comparison
                        if (guidance) {
                          const themeSizes: Record<string, string[]> = {
                            fruit: ["Poppy Seed", "Sesame Seed", "Lentil", "Blueberry", "Raspberry", "Olive", "Fig", "Lime", "Lemon", "Apple", "Avocado", "Onion", "Mango", "Banana", "Pomegranate", "Papaya", "Grapefruit", "Cantaloupe", "Cauliflower", "Coconut", "Pineapple", "Butternut Squash", "Watermelon", "Pumpkin"],
                            geek: ["8-bit Pixel", "Microchip", "D4 Die", "D20 Die", "USB Drive", "GameBoy Cartridge", "Mouse", "Smartphone", "GameBoy Color", "Communicator", "Phaser", "Sonic Screwdriver", "Pip-Boy", "Lightsaber Hilt", "VR Headset", "Droid", "Keyboard", "Laptop", "Console", "Desktop Tower"],
                            sports: ["BB Pellet", "Airsoft Pellet", "Golf Tee", "Marble", "Poker Chip", "Golf Ball", "Squash Ball", "Ping Pong Ball", "Tennis Ball", "Baseball", "Cricket Ball", "Softball", "Hockey Puck", "Curling Stone", "Volleyball", "Bowling Ball", "Basketball", "Beach Ball", "Medicine Ball"],
                            tools: ["Small Washer", "Screw Head", "Nut", "Bolt", "Allen Key", "Socket", "Spark Plug", "Tape Measure", "Screwdriver Handle", "Wrench", "Pliers", "Hammer Head", "Drill Battery", "Multimeter", "Angle Grinder", "Circular Saw Blade", "Drill", "Toolbox", "Shop Vac", "Compressor"],
                            snacks: ["Tic Tac", "Sprinkle", "Skittle", "M&M", "Gummy Bear", "Oreo", "Reese's Cup", "Granola Bar", "Pop-Tart", "Donut", "Bagel", "Burrito", "Sub Sandwich", "Large Pizza Slice", "Bucket of Popcorn", "Party Size Chips", "Cake", "Watermelon (Whole)", "Thanksgiving Turkey"]
                          };
                          const sizes = themeSizes[currentTheme];
                          const sizeIndex = Math.min(Math.max(0, Math.floor((currentWeek - 4) / 1.6)), sizes.length - 1);
                          const newSize = currentWeek < 4 ? "Microscopic" : sizes[sizeIndex];
                          setGuidance({ ...guidance, babySize: `Size of a ${newSize}` });
                        }

                        setShowThemePicker(false);
                      } catch (error) {
                        console.error('Failed to save theme:', error);
                      } finally {
                        setSavingTheme(false);
                      }
                    }}
                    disabled={savingTheme}
                    className="w-full py-3 bg-stone-900 text-white rounded-xl font-bold disabled:opacity-50 flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                  >
                    {savingTheme ? (
                      <><Loader2 size={18} className="animate-spin" /> Saving...</>
                    ) : (
                      'Save Theme'
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* FlashFacts Component - Always Visible */}
          <FlashCards
            week={currentWeek}
            role={user.role}
            onViewAll={onNavigateToTool ? () => onNavigateToTool('flashfacts') : undefined}
          />

          {/* Dynamic Cards based on Role - Only if guidance exists */}
          {guidance ? (
            <div className="space-y-4 animate-fade-in">
              {/* Card 1: Action / Self Care */}
              <div className={`${theme.actionCardBg} border ${theme.actionCardBorder} p-5 rounded-2xl relative overflow-hidden group`}>
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                  {isPartner ? <CheckSquare size={80} className={theme.actionIconColor} /> : <HeartHandshake size={80} className={theme.actionIconColor} />}
                </div>
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`${theme.actionBadgeBg} ${theme.actionBadgeText} px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide`}>
                      {isPartner ? "Action Plan" : "Self Compassion"}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-stone-900 mb-1">{isPartner ? "Do This Today" : "For You"}</h3>
                  <p className="text-stone-700">{guidance.actionItem}</p>
                </div>
              </div>

              {/* Card 2: Script / Communication */}
              <div className={`${theme.scriptCardBg} border ${theme.scriptCardBorder} p-5 rounded-2xl relative overflow-hidden group`}>
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                  <MessageSquare size={80} className={theme.scriptIconColor} />
                </div>
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`${theme.scriptBadgeBg} ${theme.scriptBadgeText} px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide`}>
                      {isPartner ? "Script" : "Communication Tip"}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-stone-900 mb-1">
                    {isPartner ? "Say This" : "Ask For This"}
                  </h3>
                  <div className={`p-3 rounded-xl border italic font-medium ${theme.scriptBoxBg}`}>
                    {guidance.script}
                  </div>
                </div>
              </div>

              {/* Card 3: Mindfulness / Extra Context */}
              <div className="bg-stone-100 p-5 rounded-2xl">
                <div className="flex items-start gap-3">
                  <Coffee className="w-5 h-5 text-stone-500 mt-1" />
                  <div>
                    <h4 className="font-bold text-stone-900 text-sm">
                      {isPartner ? "Mental Check" : "Mindfulness"}
                    </h4>
                    <p className="text-stone-600 text-sm mt-1">{guidance.selfCare}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : isPartner && weeklyPartnerTip ? (
            /* Partners get static content from partnerCoaching.ts */
            <div className="space-y-4 animate-fade-in">
              {/* Action Card */}
              <div className={`${theme.actionCardBg} border ${theme.actionCardBorder} p-5 rounded-2xl relative overflow-hidden group`}>
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                  <CheckSquare size={80} className={theme.actionIconColor} />
                </div>
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`${theme.actionBadgeBg} ${theme.actionBadgeText} px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide`}>
                      Week {currentWeek} Action
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-stone-900 mb-1">Do This Today</h3>
                  <p className="text-stone-700">{weeklyPartnerTip.doThis[0]}</p>
                </div>
              </div>

              {/* Script Card */}
              <div className={`${theme.scriptCardBg} border ${theme.scriptCardBorder} p-5 rounded-2xl relative overflow-hidden group`}>
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                  <MessageSquare size={80} className={theme.scriptIconColor} />
                </div>
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`${theme.scriptBadgeBg} ${theme.scriptBadgeText} px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide`}>
                      Script
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-stone-900 mb-1">Say This</h3>
                  <div className={`p-3 rounded-xl border italic font-medium ${theme.scriptBoxBg}`}>
                    {weeklyPartnerTip.sayThis}
                  </div>
                </div>
              </div>

              {/* Self-Care Card */}
              <div className="bg-stone-100 p-5 rounded-2xl">
                <div className="flex items-start gap-3">
                  <Coffee className="w-5 h-5 text-stone-500 mt-1" />
                  <div>
                    <h4 className="font-bold text-stone-900 text-sm">For You</h4>
                    <p className="text-stone-600 text-sm mt-1">{weeklyPartnerTip.selfCare}</p>
                  </div>
                </div>
              </div>

              {/* Sources Link for Partners */}
              <a
                href="https://www.acog.org/womens-health/pregnancy"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-stone-50 border border-stone-100 p-3 rounded-xl hover:bg-stone-100 active:scale-[0.99] transition-all flex items-center gap-2 text-[11px] text-stone-500"
              >
                <BrainCircuit className="w-3.5 h-3.5" />
                <span>Content based on <span className="font-medium text-stone-600">ACOG guidelines</span></span>
                <ChevronRight className="w-3 h-3 ml-auto text-stone-400" />
              </a>
            </div>
          ) : !isPartner && weeklyPregnancyTip ? (
            /* Pregnant users get static content from pregnancyContent.ts */
            <div className="space-y-4 animate-fade-in">
              {/* Baby Update Card */}
              <div className={`${theme.actionCardBg} border ${theme.actionCardBorder} p-5 rounded-2xl relative overflow-hidden group`}>
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                  <Baby size={80} className={theme.actionIconColor} />
                </div>
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`${theme.actionBadgeBg} ${theme.actionBadgeText} px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide`}>
                      Baby Update
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-stone-900 mb-1">Your Baby This Week</h3>
                  <p className="text-stone-700">{weeklyPregnancyTip.babyUpdate}</p>
                </div>
              </div>

              {/* Ask For This Card */}
              <div className={`${theme.scriptCardBg} border ${theme.scriptCardBorder} p-5 rounded-2xl relative overflow-hidden group`}>
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                  <MessageSquare size={80} className={theme.scriptIconColor} />
                </div>
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`${theme.scriptBadgeBg} ${theme.scriptBadgeText} px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide`}>
                      Communication
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-stone-900 mb-1">Ask For This</h3>
                  <div className={`p-3 rounded-xl border italic font-medium ${theme.scriptBoxBg}`}>
                    "{weeklyPregnancyTip.askForThis}"
                  </div>
                </div>
              </div>

              {/* Self-Care Card */}
              <div className="bg-stone-100 p-5 rounded-2xl">
                <div className="flex items-start gap-3">
                  <HeartHandshake className="w-5 h-5 text-rose-500 mt-1" />
                  <div>
                    <h4 className="font-bold text-stone-900 text-sm">Self-Care Today</h4>
                    <p className="text-stone-600 text-sm mt-1">{weeklyPregnancyTip.selfCareTip}</p>
                  </div>
                </div>
              </div>

              {/* Affirmation Card */}
              <div className="bg-rose-50 border border-rose-100 p-5 rounded-2xl">
                <div className="flex items-start gap-3">
                  <Heart className="w-5 h-5 text-rose-400 mt-1" />
                  <div>
                    <h4 className="font-bold text-stone-900 text-sm">Today's Affirmation</h4>
                    <p className="text-rose-700 text-sm mt-1 italic">"{weeklyPregnancyTip.affirmation}"</p>
                  </div>
                </div>
              </div>

              {/* Medical Sources Link */}
              <a
                href="https://www.mayoclinic.org/healthy-lifestyle/pregnancy-week-by-week"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-stone-50 border border-stone-100 p-3 rounded-xl hover:bg-stone-100 active:scale-[0.99] transition-all flex items-center gap-2 text-[11px] text-stone-500"
              >
                <Stethoscope className="w-3.5 h-3.5" />
                <span>Content sourced from <span className="font-medium text-stone-600">Mayo Clinic</span></span>
                <ChevronRight className="w-3 h-3 ml-auto text-stone-400" />
              </a>
            </div>
          ) : (
            <div className="text-center py-8 bg-stone-50 rounded-2xl border border-stone-100 border-dashed animate-fade-in">
              <CalendarOff className="w-10 h-10 text-stone-300 mx-auto mb-2" />
              <h3 className="font-medium text-stone-900">Daily Plan Missing</h3>
              <p className="text-sm text-stone-500 mt-1">
                {isToday(viewDate) ? "Check in to generate your personalized plan." : "No data for this date."}
              </p>
              {isToday(viewDate) && (
                <button
                  onClick={() => setShowCheckIn(true)}
                  className="mt-4 bg-stone-900 text-white px-6 py-3 rounded-xl font-bold text-sm shadow-lg hover:bg-stone-800 transition-all"
                >
                  Start Check-In
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Quick Assist Floating Button - Now absolute relative to this container */}
      <div className="absolute bottom-6 right-6 z-40">
        <button
          onClick={() => setShowCoach(true)}
          aria-label="Open coach chat"
          className="bg-stone-900 text-white p-4 rounded-full shadow-2xl hover:bg-stone-800 transition-all active:scale-95 flex items-center justify-center border-2 border-stone-800"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      </div>

      {/* General Coach Chat Modal - z-[60] to appear above bottom nav */}
      {showCoach && (
        <div className="fixed inset-0 z-[60] bg-stone-900/50 backdrop-blur-sm flex justify-end">
          <div className="w-full max-w-md bg-white h-full shadow-2xl relative animate-slide-in-right">
            {/* Header */}
            <div className="p-4 border-b border-stone-100 flex justify-between items-center bg-white pt-safe">
              <div className="flex items-center gap-2">
                <div className={`p-2 rounded-xl ${isPartner ? 'bg-indigo-50' : 'bg-rose-50'}`}>
                  {coachConfig.icon}
                </div>
                <div>
                  <h3 className="font-bold text-stone-900 leading-none">{coachConfig.title}</h3>
                  <p className="text-[10px] text-stone-400 font-medium mt-0.5 uppercase tracking-wide">
                    {isPartner ? `Week ${currentWeek} Partner Guide` : `Week ${currentWeek} Pregnancy Guide`}
                  </p>
                </div>
              </div>
              <button onClick={() => setShowCoach(false)} aria-label="Close coach chat" className="p-2 bg-stone-50 rounded-full text-stone-500">
                <X size={20} />
              </button>
            </div>

            {/* Scrollable content - extra padding at bottom for floating input */}
            <div className="overflow-y-auto p-4 space-y-4 bg-stone-50" style={{ height: 'calc(100vh - 140px)', paddingBottom: '160px' }}>
              {chatMessages.length === 0 && (
                <div className="mt-2 p-2">
                  {/* Intro Chat Bubble - Context for the week */}
                  {/* Intro bubble for both roles */}
                  {(coachConfig as any).intro && (
                    <div className="flex justify-start mb-4">
                      <div className={`max-w-[90%] p-4 rounded-2xl rounded-bl-sm bg-white text-stone-800 border shadow-sm ${isPartner ? 'border-indigo-100' : 'border-rose-100'}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-xs font-bold uppercase tracking-wide ${isPartner ? 'text-indigo-600' : 'text-rose-600'}`}>
                            Week {currentWeek} • {trimesterLabel}
                          </span>
                        </div>
                        <p className="text-sm leading-relaxed">{(coachConfig as any).intro}</p>
                        {isPartner && weeklyPartnerTip?.sayThis && (
                          <div className="mt-3 p-2 bg-indigo-50 rounded-lg border border-indigo-100">
                            <p className="text-xs font-medium text-indigo-700 mb-1">💬 This week, try saying:</p>
                            <p className="text-sm italic text-indigo-900">"{weeklyPartnerTip.sayThis}"</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <p className="text-stone-900 font-bold mb-1 text-center">{coachConfig.title}</p>
                  <p className="text-sm text-stone-500 mb-4 max-w-[260px] mx-auto leading-relaxed text-center">
                    {coachConfig.subtitle}
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {coachConfig.starters.map((starter, i) => (
                      <button
                        key={i}
                        onClick={() => handleCoachSend(starter)}
                        className="w-full p-3 bg-white border border-stone-200 rounded-xl text-sm text-stone-600 text-left hover:border-stone-300 active:scale-[0.98] transition-all flex justify-between items-center group"
                      >
                        <span className="line-clamp-2">"{starter}"</span>
                        <ChevronRight size={14} className="text-stone-300 group-hover:text-stone-500 flex-shrink-0 ml-1" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {/* New Topic button when chat has messages */}
              {chatMessages.length > 0 && (
                <div className="flex justify-center mb-3">
                  <button
                    onClick={() => setChatMessages([])}
                    className="px-4 py-2 text-xs font-medium text-stone-500 bg-white border border-stone-200 rounded-full hover:bg-stone-50 active:scale-95 transition-all flex items-center gap-1.5"
                  >
                    <ChevronLeft size={14} />
                    New Topic
                  </button>
                </div>
              )}
              {chatMessages.map(msg => {
                // Format message text with markdown-like syntax including links
                const formatMessage = (text: string) => {
                  return text.split('\n').map((line, lineIdx) => {
                    // First, parse markdown links [text](url)
                    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
                    const segments: (string | React.ReactNode)[] = [];
                    let lastIndex = 0;
                    let match;

                    while ((match = linkRegex.exec(line)) !== null) {
                      // Add text before the link
                      if (match.index > lastIndex) {
                        segments.push(line.slice(lastIndex, match.index));
                      }
                      // Add the link
                      segments.push(
                        <a
                          key={`link-${lineIdx}-${match.index}`}
                          href={match[2]}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline underline-offset-2 inline-flex items-center gap-0.5"
                        >
                          {match[1]}
                        </a>
                      );
                      lastIndex = match.index + match[0].length;
                    }
                    // Add remaining text after last link
                    if (lastIndex < line.length) {
                      segments.push(line.slice(lastIndex));
                    }

                    // If no links found, use original line
                    const lineContent = segments.length > 0 ? segments : [line];

                    // Now handle **bold** and _italic_ in text segments
                    const formattedLine = lineContent.map((segment, segIdx) => {
                      if (typeof segment !== 'string') return segment;

                      // Handle **bold** text
                      const parts = segment.split(/(\*\*[^*]+\*\*)/g);
                      return parts.map((part, partIdx) => {
                        if (part.startsWith('**') && part.endsWith('**')) {
                          return <strong key={`${segIdx}-${partIdx}`} className="font-semibold">{part.slice(2, -2)}</strong>;
                        }
                        // Handle _italic_ text
                        if (part.startsWith('_') && part.endsWith('_')) {
                          return <em key={`${segIdx}-${partIdx}`} className="italic text-stone-500">{part.slice(1, -1)}</em>;
                        }
                        return part;
                      });
                    });

                    return (
                      <span key={lineIdx} className="block">
                        {formattedLine}
                        {lineIdx < text.split('\n').length - 1 && <br />}
                      </span>
                    );
                  });
                };

                return (
                  <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] p-3 rounded-2xl text-sm shadow-sm leading-relaxed ${msg.sender === 'user'
                      ? 'bg-stone-800 text-white rounded-br-sm'
                      : 'bg-white text-stone-800 border border-stone-100 rounded-bl-sm'
                      }`}>
                      {msg.sender === 'user' ? msg.text : formatMessage(msg.text)}
                    </div>
                  </div>
                );
              })}
              {chatLoading && (
                <div className="flex justify-start">
                  <div className="bg-white p-3 rounded-2xl shadow-sm border border-stone-200">
                    <Loader2 className="w-4 h-4 animate-spin text-stone-400" />
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            {/* Input fixed above navbar - 80px from bottom to clear nav */}
            <div className="fixed z-[70] p-3 bg-white border-t border-stone-100 max-w-md ml-auto shadow-lg rounded-t-xl" style={{ bottom: '80px', left: 0, right: 0 }}>
              <form
                onSubmit={(e) => { e.preventDefault(); handleCoachSend(); }}
                className="flex items-center gap-2"
              >
                <input
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  className="flex-1 bg-stone-50 border-0 rounded-xl px-4 py-3 focus:ring-2 focus:ring-stone-200 outline-none text-base"
                  placeholder={isPartner ? "Ask for advice..." : "Share how you feel..."}
                />
                <button
                  type="submit"
                  disabled={!chatInput.trim() || chatLoading}
                  className={`p-3 text-white rounded-xl disabled:opacity-50 transition-colors ${coachConfig.accentColor}`}
                >
                  <Send size={20} />
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
